import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
  }

  // Initialize socket connection
  connect(userId, userType) {
    if (this.socket && this.isConnected) {
      return;
    }

    // Check if backend socket server is available, fallback gracefully
    // Prefer explicit socket URL; otherwise derive from API URL, fallback to 5001
    const serverUrl = (() => {
      const envSocket = process.env.REACT_APP_SOCKET_URL;
      const envApi = process.env.REACT_APP_API_URL;
      if (envSocket && /^https?:\/\//.test(envSocket)) return envSocket;
      if (envApi && /^https?:\/\//.test(envApi)) return envApi.replace(/\/?api$/, '');
      if (typeof window !== 'undefined') return window.location.origin.replace(/\/+$/, '');
      return 'http://localhost:5000';
    })();
    
    try {
      this.socket = io(serverUrl, {
        auth: {
          userId,
          userType
        },
        transports: ['websocket', 'polling'],
        timeout: 10000, // Reduced timeout
        forceNew: true,
        autoConnect: false // Don't auto-connect to prevent immediate errors
      });

      this.setupEventListeners();
      this.setupReconnection();
      
      // Try to connect with error handling
      this.socket.connect();
    } catch (error) {
      console.warn('Socket.IO server not available, running in offline mode:', error);
      this.handleOfflineMode();
    }
  }

  // Setup basic socket event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Emit user online status
      this.socket.emit('user_online', {
        userId: this.socket.auth.userId,
        userType: this.socket.auth.userType,
        timestamp: new Date()
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnected = false;
      
      // Emit user offline status
      if (reason !== 'io client disconnect') {
        this.handleReconnection();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.isConnected = false;
      this.handleReconnection();
    });

    // Real-time notification events
    this.socket.on('notification', (data) => {
      this.handleNotification(data);
    });

    this.socket.on('job_application', (data) => {
      this.handleJobApplication(data);
    });

    this.socket.on('service_booking', (data) => {
      this.handleServiceBooking(data);
    });

    this.socket.on('message', (data) => {
      this.handleMessage(data);
    });

    this.socket.on('approval_update', (data) => {
      this.handleApprovalUpdate(data);
    });

    this.socket.on('payment_update', (data) => {
      this.handlePaymentUpdate(data);
    });

    this.socket.on('user_status_change', (data) => {
      this.handleUserStatusChange(data);
    });
  }

  // Setup automatic reconnection
  setupReconnection() {
    if (!this.socket) return;

    this.socket.on('disconnect', () => {
      this.handleReconnection();
    });
  }

  handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(Math.pow(2, this.reconnectAttempts) * 1000, 30000); // Cap at 30 seconds
      
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        if (this.socket && !this.isConnected) {
          try {
            this.socket.connect();
          } catch (error) {
            console.warn('Reconnection failed:', error);
            this.handleOfflineMode();
          }
        }
      }, delay);
    } else {
      console.warn('Max reconnection attempts reached, switching to offline mode');
      this.handleOfflineMode();
    }
  }

  // Handle offline mode gracefully
  handleOfflineMode() {
    this.isConnected = false;
    this.reconnectAttempts = this.maxReconnectAttempts;
    
    // Notify listeners that we're in offline mode
    this.notifyListeners('connection_status', {
      status: 'offline',
      message: 'Running in offline mode - real-time features disabled'
    });
    
    console.info('Application running in offline mode - real-time features disabled');
  }

  // Event handlers for different notification types
  handleNotification(data) {
    console.log('Received notification:', data);
    this.notifyListeners('notification', data);
    
    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(data.title, {
        body: data.message,
        icon: '/favicon.ico',
        tag: data.id
      });
    }
  }

  handleJobApplication(data) {
    console.log('Job application update:', data);
    this.notifyListeners('job_application', data);
    
    // Trigger notification
    this.handleNotification({
      id: `job_app_${data.applicationId}`,
      type: 'job_application',
      title: 'New Job Application',
      message: `${data.applicantName} applied for ${data.jobTitle}`,
      data: data
    });
  }

  handleServiceBooking(data) {
    console.log('Service booking update:', data);
    this.notifyListeners('service_booking', data);
    
    // Trigger notification
    this.handleNotification({
      id: `service_booking_${data.bookingId}`,
      type: 'service_booking',
      title: 'New Service Booking',
      message: `${data.clientName} booked ${data.serviceName}`,
      data: data
    });
  }

  handleMessage(data) {
    console.log('New message:', data);
    this.notifyListeners('message', data);
    
    // Trigger notification for new messages
    this.handleNotification({
      id: `message_${data.messageId}`,
      type: 'message',
      title: `New message from ${data.senderName}`,
      message: data.content,
      data: data
    });
  }

  handleApprovalUpdate(data) {
    console.log('Approval update:', data);
    this.notifyListeners('approval_update', data);
    
    // Trigger notification
    this.handleNotification({
      id: `approval_${data.itemId}`,
      type: 'approval',
      title: `${data.itemType} ${data.status}`,
      message: data.message,
      data: data
    });
  }

  handlePaymentUpdate(data) {
    console.log('Payment update:', data);
    this.notifyListeners('payment_update', data);
    
    // Trigger notification
    this.handleNotification({
      id: `payment_${data.transactionId}`,
      type: 'payment',
      title: 'Payment Update',
      message: `Payment ${data.status}: ${data.amount}`,
      data: data
    });
  }

  handleUserStatusChange(data) {
    console.log('User status change:', data);
    this.notifyListeners('user_status_change', data);
  }

  // Emit events to server with safety checks
  emitJobApplication(applicationData) {
    if (this.socket && this.isConnected) {
      this.socket.emit('job_application_submitted', applicationData);
    } else {
      console.warn('Socket not connected, job application event not sent');
    }
  }

  emitServiceBooking(bookingData) {
    if (this.socket && this.isConnected) {
      this.socket.emit('service_booking_created', bookingData);
    } else {
      console.warn('Socket not connected, service booking event not sent');
    }
  }

  emitMessage(messageData) {
    if (this.socket && this.isConnected) {
      this.socket.emit('send_message', messageData);
    } else {
      console.warn('Socket not connected, message event not sent');
    }
  }

  emitTyping(conversationId, isTyping) {
    if (this.socket && this.isConnected) {
      this.socket.emit('typing', { conversationId, isTyping });
    } else {
      console.warn('Socket not connected, typing event not sent');
    }
  }

  emitStopTyping(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('stop_typing', { conversationId });
    } else {
      console.warn('Socket not connected, stop typing event not sent');
    }
  }

  emitUserStatusUpdate(status) {
    if (this.socket && this.isConnected) {
      this.socket.emit('user_status_update', {
        userId: this.socket.auth.userId,
        status,
        timestamp: new Date()
      });
    } else {
      console.warn('Socket not connected, user status change event not sent');
    }
  }

  // Join/leave rooms for targeted notifications
  joinRoom(roomId) {
    if (this.isConnected) {
      this.socket.emit('join_room', roomId);
    }
  }

  leaveRoom(roomId) {
    if (this.isConnected) {
      this.socket.emit('leave_room', roomId);
    }
  }

  // Subscribe to specific events
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    return () => {
      this.unsubscribe(event, callback);
    };
  }

  unsubscribe(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Notify all listeners for an event
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in socket listener:', error);
        }
      });
    }
  }

  // Request browser notification permission
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.emit('user_offline', {
        userId: this.socket.auth.userId,
        timestamp: new Date()
      });
      
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  // Cleanup
  cleanup() {
    this.disconnect();
    this.listeners.clear();
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;